import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import { ChatContext } from './chat-context';
import {
  createChatTokenSimple,
  createChatTokenTemplateSimple,
  createChatTokenConfigSimple,
  getAiSaver, getTemplateCode, getTemplateCodeV2,
  commitCreateDoc as api_commitCreateDoc, getSiteConfig,
} from '../../../cms-api/ai';
import { aiWsUrl } from '../../../cms-api/ai-relay';
import { getPage, getPost } from '../../../cms-api/page';
import { getTemplate } from '../../../cms-api/template';
import { useSiteContext } from '../../../components/site/context/site-context';
import { createExponentialBackoff, createTokenRetryHandler } from '../../../utils/retry-utils';
import {
  isSitePermissionDenied,
  SITE_PERMISSION_DENIED_MESSAGE,
  WS_CLOSE_INVALID_OR_FORBIDDEN,
} from '../../../utils/cms-api-error';

const initialState = {
  loading: false,
};

const MSG_ID_RE = /^\[MSG_ID:([0-9a-fA-F-]{36})\]\s/;

// 裏に回ったタブで空いている WS を閉じるまでの猶予。タブを行き来するだけで
// 切って繋ぎ直さないためのもの。
export const HIDDEN_DISCONNECT_DELAY_MS = 30000;

const isPageVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

export function ChatProvider({ children, type_str, config_data, set_template_code, get_template_code, get_site_config_data }) {

  const { site, getTempConfig, saveTempConfig } = useSiteContext();
  const { enqueueSnackbar } = useSnackbar();

  const [type, setType] = useState(type_str);

  const [config, setConfig] = useState(config_data);

  const [state, setState] = useState(initialState);

  const [isSending, setIsSending] = useState(false);

  const [socketUrl, setSocketUrl] = useState(null);

  const [createToken, setCreateToken] = useState(null);

  const [createTokenLoading, setCreateTokenLoading] = useState(false);

  // サイトの閲覧権限が無く cms_ai に弾かれた。true の間は token を作り直さない
  // （再試行や欄の開き直しで 403 を繰り返し叩かないため）。サイトを切り替えたら下ろす。
  const [accessDenied, setAccessDenied] = useState(false);

  // WS が "Invalid token"（4001）で閉じられた。開いたままの欄で token を作り直す合図。
  const [tokenInvalidated, setTokenInvalidated] = useState(false);

  const [lastMessageId, setLastMessageId] = useState(null);
  const [intermediateProcessMessage, setIntermediateProcessMessage] = useState(null);

  const [templateHistory, setTemplateHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [hasTemplateChanges, setHasTemplateChanges] = useState(false);

  const seenMessageIdsRef = useRef(new Set());

  const addMessageFuncRef = useRef(null)

  const messageStatusRef = useRef(null)

  const formatMessageRef = useRef(null)

  const websocketBackoffRef = useRef(createExponentialBackoff(3000, 30000))

  const tokenRetryHandlerRef = useRef(createTokenRetryHandler(2000, 60000, 5))

  // WS はチャット欄が開いていて（chatActive）タブが見えている間だけ張る。
  // 管理画面の全ページに常駐する simple チャットが、開かれてもいないのに
  // 張りっぱなしになり、Chrome が裏のタブを凍結→再開するたび（約 1 分ごと）に
  // 全 WS を切られて再接続を繰り返していた（cms_ai 側で 1 本ごとに
  // Firestore のセッション開始と 200ms の reader_loop が走る）。
  // 会話の途中（isSending）だけは欄を閉じても裏に回っても切らない。
  // 繋ぎ直しは同じ chat_token の URL なので、履歴は画面側に残り、
  // サーバの再送は MSG_ID で重複を捨てる。
  const [chatActive, setChatActive] = useState(false);
  const [pageVisible, setPageVisible] = useState(isPageVisible);
  // 裏に回ってから猶予が過ぎた、または裏で切れた（再接続しなかった）。
  // 見えるようになったら下ろす。URL を一度 null にしてから戻すことで
  // react-use-websocket に新しい接続を張らせる。
  const [pausedWhileHidden, setPausedWhileHidden] = useState(false);

  const canReconnect = isSending || (chatActive && pageVisible);
  const canReconnectRef = useRef(canReconnect);
  canReconnectRef.current = canReconnect;

  const connectedSocketUrl = (isSending || (chatActive && !pausedWhileHidden)) ? socketUrl : null;

  useEffect(() => {
    setAccessDenied(false);
  }, [site?.site_id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = isPageVisible();
      setPageVisible(visible);
      if (visible) {
        setPausedWhileHidden(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (pageVisible) {
      return undefined;
    }
    const timer = setTimeout(() => setPausedWhileHidden(true), HIDDEN_DISCONNECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pageVisible]);

  const { sendMessage, lastMessage, readyState } = useWebSocket(connectedSocketUrl, {
    onError: (event) => {
      console.error("WebSocket error:", event);
    },
    onClose: (event) => {
      console.warn("WebSocket close:", event);
      if (event?.code !== WS_CLOSE_INVALID_OR_FORBIDDEN) {
        return;
      }
      // 4001 は再接続しても同じ結果になる。URL と token を捨てて止める。
      setSocketUrl(null);
      setCreateToken(null);
      setIsSending(false);
      setIntermediateProcessMessage(null);
      if (event.reason === 'Forbidden') {
        setAccessDenied(true);
        enqueueSnackbar(SITE_PERMISSION_DENIED_MESSAGE, { variant: 'error' });
      } else {
        // "Invalid token": 権限の問題ではないので、欄が開いていれば token を作り直す
        setTokenInvalidated(true);
      }
    },
    onOpen: () => {
      console.log("WebSocket connected successfully");
      websocketBackoffRef.current.reset();
    },
    shouldReconnect: (closeEvent) => {
      // const backoff = websocketBackoffRef.current;
      // if (backoff.hasReachedMaxRetries()) {
      //   console.error("WebSocket max reconnection attempts reached");
      //   return false;
      // }
      console.warn("WebSocket close event:", closeEvent);
      // ⚠ 4001（Invalid token / Forbidden）は何度繋いでも閉じられる。
      // true を返すと約 3 秒ごとに無限に再接続する。
      if (closeEvent?.code === WS_CLOSE_INVALID_OR_FORBIDDEN) {
        return false;
      }
      if (!canReconnectRef.current) {
        // 裏のタブで空いている間に切れた。見えるようになるまで繋ぎ直さない。
        // 欄を閉じて URL を null にした自前の切断もここを通るので、
        // 見えている間は印を付けない（付けると次に欄を開いても繋がらない）。
        if (!isPageVisible()) {
          setPausedWhileHidden(true);
        }
        return false;
      }
      return true;
    },
    // reconnectAttempts: 10,
    reconnectInterval: () => {
      const delay = websocketBackoffRef.current.getDelay();
      const safeDelay = delay ?? 30000;
      console.warn(`WebSocket reconnecting in ${safeDelay}ms`);
      return safeDelay;
    },
    // onerror 側の再接続は shouldReconnect を通らないので、同じ条件で止める。
    retryOnError: canReconnect,
  });

  const setAddMessageFunc = useCallback((func) => {
    addMessageFuncRef.current = func;
  }, []);

  const callAddMessageFunc = useCallback((...args) => {
    if (addMessageFuncRef.current) {
      addMessageFuncRef.current(...args);
    }
  }, []);

  const setMessageStatusFunc = useCallback((func) => {
    messageStatusRef.current = func;
  }, [])

  const callMessageStatusFunc = useCallback((...args) => {
    if (messageStatusRef.current) {
      messageStatusRef.current(...args);
    }
  }, [])

  const setFormatMessageFunc = useCallback((func) => {
    formatMessageRef.current = func;
  }, [])

  const callFormatMessageFunc = useCallback((...args) => {
    if (formatMessageRef.current) {
      formatMessageRef.current(...args);
    }
  }, [])

  // token 作成失敗の共通処理。
  // ⚠ 権限なし（403）は再試行しても結果が変わらないので再試行しない。再試行の途中で
  // 権限エラーになった場合も、retryHandler は例外を握って次の再試行を積むため、
  // ここで例外を飲み込んで「成功」として返し、再試行の連鎖を止める。
  const handleTokenCreationError = async (error, executeTokenCreation, label) => {
    console.error(`Error creating ${label}:`, error);

    const onPermissionDenied = () => {
      setAccessDenied(true);
      enqueueSnackbar(SITE_PERMISSION_DENIED_MESSAGE, { variant: 'error' });
      setCreateTokenLoading(false);
      setIsSending(false);
    };

    if (isSitePermissionDenied(error)) {
      onPermissionDenied();
      return;
    }

    const retryHandler = tokenRetryHandlerRef.current;
    let success = false;
    if (!retryHandler.hasReachedMaxRetries()) {
      success = await retryHandler.retry(async () => {
        try {
          await executeTokenCreation();
        } catch (retryError) {
          if (isSitePermissionDenied(retryError)) {
            onPermissionDenied();
            return;
          }
          throw retryError;
        }
      });
    }
    if (!success) {
      setCreateTokenLoading(false);
      setIsSending(false);
      enqueueSnackbar('AI チャットに接続できませんでした。時間をおいてチャット欄を開き直してください。', { variant: 'error' });
    }
  };

  const createTokenCallbackSimple = async (site_id) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenSimple({
        site_id,
        address: saverData.address,
      });

      setCreateToken(createTokenData);
      setSocketUrl(aiWsUrl(saverData.address, `/chat/${createTokenData.chat_token}/simple/chat`));
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      await handleTokenCreationError(error, executeTokenCreation, 'chat token');
    }
  }
  
  const createTokenCallbackConfigSimple = async (site_id) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenConfigSimple({
        site_id,
        address: saverData.address,
      });

      setCreateToken(createTokenData);
      setSocketUrl(aiWsUrl(saverData.address, `/config/chat/${createTokenData.chat_token}/simple/chat`));
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      await handleTokenCreationError(error, executeTokenCreation, 'config chat token');
    }
  }


  const createTokenCallbackTemplateSimple = async (site_id,
                                                   page_pkg_code,
                                                   template_pkg_code,
                                                   page_type,
                                                   is_list_page,
                                                   template_page_id
  ) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenTemplateSimple({
        site_id,
        address: saverData.address,
        page_pkg_code,
        template_pkg_code,
        page_type,
        is_list_page,
        template_page_id,
      });

      setCreateToken(createTokenData);
      setSocketUrl(aiWsUrl(saverData.address, `/template/chat/${createTokenData.chat_token}/simple/chat`));
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      await handleTokenCreationError(error, executeTokenCreation, 'template chat token');
    }
  }
  const createTokenCallbackTemplateSimpleV2 = async (site_id,
                                                   page_pkg_code,
                                                   template_pkg_code,
                                                   page_type,
                                                   is_list_page,
                                                   template_page_id
  ) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      
      let template_layout_code = null;
      
      if (template_page_id) {
        try {
          const getPageFunc = page_type === 2 ? getPost : getPage;
          
          const pageData = await getPageFunc({
            site_id,
            page_id: template_page_id,
            page_pkg_code,
          });
          
          let tpl_code;
          if (page_type === 2) {
            tpl_code = is_list_page ? pageData.list_tpl_code : pageData.detail_tpl_code;
          } else {
            tpl_code = pageData.tpl_code;
          }
          
          if (tpl_code) {
            const templateData = await getTemplate({
              site_id,
              template_pkg_code,
              tpl_code,
            });
            
            template_layout_code = templateData.layout_code;
          }
        } catch (error) {
          console.error("Error fetching page data or template layout code:", error);
        }
      }
      
      const createTokenParams = {
        site_id,
        address: saverData.address,
        page_pkg_code,
        template_pkg_code,
        page_type,
        is_list_page,
        template_page_id,
      };
      
      if (template_layout_code) {
        createTokenParams.template_layout_code = template_layout_code;
      }
      
      const createTokenData = await createChatTokenTemplateSimple(createTokenParams);

      setCreateToken(createTokenData);
      setSocketUrl(aiWsUrl(saverData.address, `/template/chat/${createTokenData.chat_token}/v2/simple/chat`));
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      await handleTokenCreationError(error, executeTokenCreation, 'template chat token (v2)');
    }
  }


  const createTokenCallback = useCallback(async () => {
    if (accessDenied) {
      return;
    }
    if (!createTokenLoading && !createToken) {
      tokenRetryHandlerRef.current.reset();
      setCreateTokenLoading(true);

      try {
        if (type === "template-simple") {
          await createTokenCallbackTemplateSimple(
            site.site_id,
            config.page_pkg_code,
            config.template_pkg_code,
            config.page_type,
            config.is_list_page,
            config.template_page_id,
          );
        } else if (type === "template-simple-v2") {
          await createTokenCallbackTemplateSimpleV2(
            site.site_id,
            config.page_pkg_code,
            config.template_pkg_code,
            config.page_type,
            config.is_list_page,
            config.template_page_id,
          );
        } else if (type === "config-simple") {
          await createTokenCallbackConfigSimple(
            site.site_id,
          );
        } else {
          await createTokenCallbackSimple(site.site_id);
        }
      } finally {
        setCreateTokenLoading(false);
      }
    }
  // createTokenCallback* は毎レンダー作り直される関数で、状態の setter と ref しか使わないため依存に含めない。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, createToken, createTokenLoading, accessDenied, type, config]);

  // 会話を送れない状態か。権限なしで弾かれた、または作成が終わったのに token が無い（作成失敗）。
  // ⚠ 作成中（createTokenLoading）は含めない。その間の送信は react-use-websocket が溜めて、繋がってから送る。
  const chatUnavailable = accessDenied || (!createToken && !createTokenLoading);

  // 送れないときの通知。MessageParser が送信の前に呼ぶ（呼ばないと送信中の表示が消えず固まる）。
  const notifyChatUnavailable = useCallback(() => {
    enqueueSnackbar(
      accessDenied
        ? SITE_PERMISSION_DENIED_MESSAGE
        : 'AI チャットに接続できていません。チャット欄を開き直してください。',
      { variant: 'error' },
    );
  }, [accessDenied, enqueueSnackbar]);

  // WS が "Invalid token" で閉じられたあと、開いたままの欄で token を作り直す。
  // 先に tokenInvalidated を下ろすので、4001 1 回につき 1 回しか走らない。
  useEffect(() => {
    if (tokenInvalidated && chatActive && !createToken && !createTokenLoading && !accessDenied) {
      setTokenInvalidated(false);
      createTokenCallback();
    }
  }, [tokenInvalidated, chatActive, createToken, createTokenLoading, accessDenied, createTokenCallback]);

  const commitCreateDoc = useCallback(async (chat_token, doc, callbackOk) => {
    const saverData = await getAiSaver();
    const re_data = await api_commitCreateDoc({
      address: saverData.address,
      chat_token,
      doc,
    });
    if (re_data.status === "OK") {
      callbackOk();
    }
  }, []);

  const getCode = useCallback(async (chat_token) => {

    if (type === "template-simple") {
      const saverData = await getAiSaver();
      const decode_code = await getTemplateCode({
        address: saverData.address,
        chat_token,
      });

      set_template_code(decode_code.code);
    } else if (type === "template-simple-v2") {
      const saverData = await getAiSaver();
      const decode_code = await getTemplateCodeV2({
        address: saverData.address,
        chat_token,
      });

      set_template_code(decode_code.code);
    }
    return "";
  }, [type, set_template_code]);

  const saveTemplateHistory = useCallback((code) => {
    if (!code) return;
    
    if (templateHistory.length > 0) {
      const lastHistoryItem = templateHistory[templateHistory.length - 1];
      if (lastHistoryItem.code === code) {
        return; // Don't add duplicate
      }
    }
    
    const newHistory = historyIndex >= 0 
      ? templateHistory.slice(0, historyIndex + 1)
      : [];
    
    const historyItem = {
      code,
      timestamp: new Date().getTime(),
      chatToken: createToken?.chat_token
    };
    
    const updatedHistory = [...newHistory, historyItem];
    
    const MAX_HISTORY_SIZE = 10;
    if (updatedHistory.length > MAX_HISTORY_SIZE) {
      updatedHistory.shift();
    }

    setTemplateHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
    setHasTemplateChanges(true);

    if (createToken?.chat_token) {
      const storageKey = `template_history_${createToken.chat_token}`;
      const historyData = {
        history: updatedHistory,
        historyIndex: updatedHistory.length - 1,
        hasChanges: true,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(storageKey, JSON.stringify(historyData));
    }
  }, [templateHistory, historyIndex, createToken]);


  const getCodeCallback = useCallback(async (chat_token) => {
    if (type === "template-simple" || type === "template-simple-v2") {
      await getCode(chat_token);
      
      if (get_template_code) {
        const newCode = get_template_code();
        saveTemplateHistory(newCode);
      }
      
      enqueueSnackbar('テンプレートコードが更新されました。履歴から前のバージョンを確認できます。', { 
        variant: 'success' 
      });
    }
  }, [type, getCode, saveTemplateHistory, enqueueSnackbar, get_template_code]);

  useEffect(() => {
    if (lastMessage?.data) {
      const raw = lastMessage.data;
      const m = raw.match(MSG_ID_RE);
      const msgId = m ? m[1] : null;
      if (msgId) {
        const set = seenMessageIdsRef.current;
        if (set.has(msgId)) {
          return;
        }
        if (set.size > 2000) set.clear();
        set.add(msgId);
      }

      const text = raw.replace(MSG_ID_RE, "");

      if (text.startsWith("ss-json:")) {
        const message = JSON.parse(text.substring(8));
        callFormatMessageFunc(message);
      } else if (text.startsWith("<start>:")) {
        setIsSending(true);
        setLastMessageId(text);
      } else if (text.startsWith("[INTERMEDIATE_PROCESS] ")) {
        const processMessage = text.substring(23);
        setIntermediateProcessMessage(processMessage);
        setIsSending(true);
      } else if (text.endsWith(":end") || text.endsWith(":wait")) {
        callMessageStatusFunc(text);
        if (text.endsWith(":end") || text === "create_doc_code:wait") {
          setIntermediateProcessMessage(null);

          if (type === "config-simple" && text.endsWith("config_edit:end")) {
            getAiSaver().then((saverData)=> {
              getSiteConfig({
                "address": saverData.address,
                "chat_token": createToken.chat_token,
              }).then((configData) => {
                saveTempConfig(configData.site_config);
              }).finally(()=>{
                setIsSending(false);
              })
            }).then(() => {
              setIsSending(false);
            })
          } else if (text.endsWith("create_code:end")) {
            getCodeCallback(createToken.chat_token);
            setIsSending(false);
          } else {
            setIsSending(false);
          }
        }
      } else {
        callAddMessageFunc(text);
        setIsSending(true);
        setIntermediateProcessMessage(null);
      }
    }
  }, [lastMessage, lastMessageId,
    callAddMessageFunc, callMessageStatusFunc,
    callFormatMessageFunc, type,
    createToken, saveTempConfig, getCodeCallback ]);

  useEffect(() => {
    if (createToken?.chat_token && (type === "template-simple" || type === "template-simple-v2")) {
      const storageKey = `template_history_${createToken.chat_token}`;
      const storedData = localStorage.getItem(storageKey);
      
      if (storedData) {
        try {
          const historyData = JSON.parse(storedData);
          setTemplateHistory(historyData.history || []);
          setHistoryIndex(historyData.historyIndex || -1);
          setHasTemplateChanges(historyData.hasChanges || false);
        } catch (error) {
          console.error('Error parsing template history:', error);
        }
      }
    }
  }, [createToken, type]);


  const memoizedValue = useMemo(
    () => ({
      loading: state.loading,
      isSending,
      createTokenLoading,
      accessDenied,
      chatUnavailable,
      notifyChatUnavailable,
      setIsSending,
      sendMessage,
      lastMessage,
      createTokenCallback,
      setChatActive,
      getCodeCallback,
      type,
      readyState,
      createToken,
      set_template_code,
      get_template_code,
      get_site_config_data,
      commitCreateDoc,
      lastMessageId,
      setAddMessageFunc,
      setMessageStatusFunc,
      setFormatMessageFunc,
      templateHistory,
      historyIndex,
      hasTemplateChanges,
      saveTemplateHistory,
      config,
      intermediateProcessMessage,
    }),
    [
      state.loading,
      isSending,
      setIsSending,
      sendMessage,
      lastMessage,
      createTokenCallback,
      setChatActive,
      getCodeCallback,
      createTokenLoading,
      accessDenied,
      chatUnavailable,
      notifyChatUnavailable,
      type,
      readyState,
      createToken,
      set_template_code,
      get_template_code,
      get_site_config_data,
      commitCreateDoc,
      lastMessageId,
      setAddMessageFunc,
      setMessageStatusFunc,
      setFormatMessageFunc,
      templateHistory,
      historyIndex,
      hasTemplateChanges,
      saveTemplateHistory,
      config,
      intermediateProcessMessage,
    ]
  );

  return <ChatContext.Provider value={memoizedValue}>{children}</ChatContext.Provider>;
}

ChatProvider.propTypes = {
  children: PropTypes.node,
  type_str: PropTypes.string.isRequired,
  config_data: PropTypes.object,
  set_template_code: PropTypes.func,
  get_template_code: PropTypes.func,
  get_site_config_data: PropTypes.func,
};
